import { useEffect } from "react";

// Google Tag Manager Configuration
const GTM_CONTAINER_ID =
  process.env.REACT_APP_GTM_CONTAINER_ID || "GTM-TKN6RZWJ";

// Función para inicializar Google Tag Manager
export const initGTM = () => {
  console.log("initGTM called with:", {
    GTM_CONTAINER_ID,
    envVar: process.env.REACT_APP_GTM_CONTAINER_ID,
    NODE_ENV: process.env.NODE_ENV,
  });

  // Verificar si ya está cargado
  if (window.dataLayer && window.GTM_INITIALIZED) {
    console.log("GTM already initialized, skipping");
    return;
  }

  // En desarrollo, crear mock dataLayer
  if (process.env.NODE_ENV === "development") {
    console.log("Development mode: Creating mock GTM dataLayer");
    window.dataLayer = window.dataLayer || [];
    window.GTM_INITIALIZED = true;
    return;
  }

  console.log("Loading Google Tag Manager for production...");

  // Inicializar dataLayer antes de cargar GTM
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });

  // Crear script de GTM
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;

  // Handlers de error y carga
  script.onerror = (error) => {
    console.warn("Failed to load Google Tag Manager script:", error);
    console.warn("Script src was:", script.src);
    // Crear fallback dataLayer
    window.dataLayer = window.dataLayer || [];
    window.GTM_INITIALIZED = true;
  };

  script.onload = () => {
    console.log("GTM script loaded successfully");
    window.GTM_INITIALIZED = true;
  };

  // Timeout como fallback
  setTimeout(() => {
    if (!window.GTM_INITIALIZED) {
      console.warn("GTM script timeout, creating fallback dataLayer");
      window.dataLayer = window.dataLayer || [];
      window.GTM_INITIALIZED = true;
    }
  }, 5000);

  document.head.appendChild(script);
};

// Función para enviar eventos a GTM/GA4 via dataLayer
export const pushToDataLayer = (eventData) => {
  if (!window.dataLayer) {
    console.warn("DataLayer not available, initializing...");
    window.dataLayer = [];
  }

  console.log("Pushing to dataLayer:", eventData);
  window.dataLayer.push(eventData);
};

// Eventos específicos para GTM
export const gtmTrackEvent = (eventName, parameters = {}) => {
  pushToDataLayer({
    event: eventName,
    ...parameters,
  });
};

// Eventos de conversión específicos para GTM
export const gtmTrackDownloadApp = (
  source = "unknown",
  platform = "unknown"
) => {
  gtmTrackEvent("download_app", {
    event_category: "conversion",
    event_label: `${source}_${platform}`,
    conversion_source: source,
    app_platform: platform,
    conversion_value: 10,
    conversion_id: "app_download",
  });
};

export const gtmTrackWhatsAppClick = (
  source = "unknown",
  context = "general"
) => {
  gtmTrackEvent("whatsapp_click", {
    event_category: "conversion",
    event_label: `${source}_${context}`,
    contact_method: "whatsapp",
    lead_source: source,
    contact_context: context,
    conversion_value: 5,
    conversion_id: "whatsapp_contact",
  });
};

export const gtmTrackFormSubmission = (
  formType = "contact",
  source = "unknown"
) => {
  gtmTrackEvent("form_submit", {
    event_category: "conversion",
    event_label: `${formType}_${source}`,
    form_type: formType,
    lead_source: source,
    conversion_value: 15,
    conversion_id: "form_submission",
  });
};

export const gtmTrackPhoneClick = (source = "unknown") => {
  gtmTrackEvent("phone_click", {
    event_category: "conversion",
    event_label: source,
    contact_method: "phone",
    lead_source: source,
    conversion_value: 8,
    conversion_id: "phone_contact",
  });
};

export const gtmTrackPageView = (pageTitle = "", pagePath = "") => {
  gtmTrackEvent("page_view", {
    page_title: pageTitle || document.title,
    page_location: pagePath || window.location.href,
    page_path: pagePath || window.location.pathname,
  });
};

// Eventos de engagement específicos
export const gtmTrackVideoPlay = (videoId = "unknown", source = "unknown") => {
  gtmTrackEvent("video_play", {
    event_category: "engagement",
    event_label: `${videoId}_${source}`,
    video_id: videoId,
    media_type: "video",
    engagement_level: "high",
  });
};

export const gtmTrackServiceSelection = (
  serviceType = "unknown",
  source = "unknown"
) => {
  gtmTrackEvent("service_selection", {
    event_category: "engagement",
    event_label: `${serviceType}_${source}`,
    service_type: serviceType,
    user_intent: "service_inquiry",
    engagement_level: "high",
  });
};

export const gtmTrackLocationSearch = (
  location = "unknown",
  source = "unknown"
) => {
  gtmTrackEvent("location_search", {
    event_category: "engagement",
    event_label: `${location}_${source}`,
    searched_location: location,
    user_intent: "local_service",
    engagement_level: "medium",
  });
};

// Core Web Vitals para GTM
export const gtmTrackCoreWebVitals = ({ name, delta, value, id, rating }) => {
  gtmTrackEvent("web_vitals", {
    event_category: "performance",
    event_label: rating, // good, needs-improvement, poor
    metric_name: name,
    metric_value: Math.round(value),
    metric_rating: rating,
    metric_id: id,
    metric_delta: Math.round(delta),
  });
};

// Hook personalizado para GTM
export const useGoogleTagManager = () => {
  useEffect(() => {
    initGTM();
  }, []);

  return {
    gtmTrackEvent,
    gtmTrackPageView,
    gtmTrackDownloadApp,
    gtmTrackWhatsAppClick,
    gtmTrackFormSubmission,
    gtmTrackPhoneClick,
    gtmTrackVideoPlay,
    gtmTrackServiceSelection,
    gtmTrackLocationSearch,
    gtmTrackCoreWebVitals,
    pushToDataLayer,
  };
};

// Componente para inicialización automática de GTM
const GoogleTagManager = () => {
  useGoogleTagManager();
  return null;
};

export default GoogleTagManager;
